/**
 * Infer whether the AI validation narrative supports or contradicts the hero summary.
 * Prevents "Evidence Retrieved + Confidence: medium" when the answer says nothing matches.
 */

const NEGATIVE_PATTERNS = [
    { id: 'no_evidence', re: /\bno evidence\b/i, weight: 2 },
    { id: 'not_available', re: /\bnot available\b|\bno specific information\b|\bis unavailable\b/i, weight: 2 },
    {
        id: 'not_address',
        re: /\bdoes not directly address\b|\bdoes not address\b|\bnot directly address\b|\bevidence provided does not (?:support|substantiate|corroborate|confirm|contain)\b/i,
        weight: 2,
    },
    { id: 'not_mention', re: /\bnot mention\b|\bno mention\b|\bnot found in\b|\bnothing in the provided\b/i, weight: 2 },
    { id: 'unrelated', re: /\bunrelated\b|\bnot related to\b/i, weight: 1 },
    {
        id: 'cannot_validate',
        re: /\bfurther assistance\b|\bclarify your request\b|\bprovide additional\b|\bunable to validate\b/i,
        weight: 1,
    },
    {
        id: 'deflection',
        re: /\b(?:query|request|question) is unclear\b|\bcould you please specify\b|\bwhat information\b[^.]{0,60}\b(?:seeking|you are seeking)\b|\bare you asking about\b|\bplease (?:clarify|specify) (?:what|which)\b|\bi(?:'m| am) not sure what\b|\bdoes not include a specific user question\b|\bclarify or restate your query\b|\brestate your query\b|\bno specific user question\b/i,
        weight: 4,
    },
    { id: 'no_blocks', re: /\bno evidence in the provided blocks\b|\bnot available in the evidence\b/i, weight: 3 },
    {
        id: 'self_irl_proof',
        re: /\baccording to the response provided\b|\bresponse provided in\b|\bidentified as\b[^.]{0,80}\baccording to\b|\bper the (?:management |)response in\b/i,
        weight: 3,
    },
];

const POSITIVE_PATTERNS = [
    /\bsupport(?:s|ed|ing)?\b.{0,40}\b(?:evidence|document|vdr|data room)\b/i,
    /\bcorroborat/i,
    /\bconsistent with\b/i,
    /\balign(?:s|ed)? with\b/i,
    /\bconfirm(?:s|ed|ing)?\b/i,
    /\bsubstantiat/i,
];

function countPatternMatches(text, patterns) {
    let score = 0;
    const hits = [];
    for (const p of patterns) {
        const re = p.re || p;
        const weight = p.weight ?? 1;
        if (re.test(text)) {
            score += weight;
            hits.push(p.id || 'positive');
        }
    }
    return { score, hits };
}

/**
 * @param {string} answerText
 * @param {{ vdrDocCount?: number, relatedIrlCount?: number, vdrHits?: number }} context
 */
export function analyzeValidationNarrative(answerText, context = {}) {
    const text = String(answerText || '').trim();
    const lower = text.toLowerCase();
    const vdrDocCount = Number(context.vdrDocCount) || 0;
    const relatedIrlCount = Number(context.relatedIrlCount) || 0;
    const vdrHits = Number(context.vdrHits) || 0;

    if (!text || text.length < 40) {
        return {
            severity: 'unknown',
            overridesSummary: false,
            documentaryMatchLabel: null,
            validationSignalLabel: null,
            reasons: [],
            negativeScore: 0,
            positiveScore: 0,
        };
    }

    const neg = countPatternMatches(lower, NEGATIVE_PATTERNS);
    let posScore = 0;
    for (const re of POSITIVE_PATTERNS) {
        if (re.test(lower)) posScore += 1;
    }

    const reasons = NEGATIVE_PATTERNS.filter(p => p.re.test(lower)).map(p => {
        switch (p.id) {
            case 'no_evidence':
                return 'AI found no supporting evidence in retrieved material';
            case 'not_available':
                return 'Requested information not available in documentary evidence';
            case 'not_address':
                return 'AI states the evidence does not address this management claim';
            case 'not_mention':
                return 'Management claim topic not found in VDR documents';
            case 'unrelated':
                return 'Retrieved evidence appears unrelated to this claim';
            case 'cannot_validate':
                return 'AI could not complete validation from available evidence';
            case 'deflection':
                return 'AI asked for clarification instead of validating the management claim';
            case 'no_blocks':
                return 'No documentary proof in the evidence blocks used for this answer';
            case 'self_irl_proof':
                return 'AI cited the management response itself — not VDR documentary proof';
            default:
                return 'Narrative indicates weak or missing documentary support';
        }
    });

    const hasDeflection = neg.hits.includes('deflection');
    const strongNegative = neg.score >= 3 || (neg.score >= 2 && posScore === 0);
    const mildNegative = neg.score >= 1 && posScore === 0;

    let severity = 'unknown';
    let overridesSummary = false;
    let documentaryMatchLabel = null;
    let validationSignalLabel = null;

    if (vdrDocCount === 0 && vdrHits === 0) {
        severity = 'none';
        overridesSummary = true;
        documentaryMatchLabel = 'Documentary match: None';
        validationSignalLabel =
            relatedIrlCount > 0
                ? 'Related IRL context only'
                : 'No VDR evidence — not documentary validated';
        if (!reasons.length) {
            reasons.push('No documentary (VDR) evidence was retrieved for this claim');
        }
    } else if (hasDeflection) {
        severity = 'none';
        overridesSummary = true;
        documentaryMatchLabel =
            vdrDocCount > 0
                ? `Documentary match: AI did not complete validation (${vdrDocCount} chunks retrieved)`
                : 'Documentary match: Validation incomplete';
        validationSignalLabel = 'Validation incomplete — AI did not answer';
    } else if (strongNegative) {
        severity = 'none';
        overridesSummary = true;
        documentaryMatchLabel =
            vdrDocCount > 0
                ? `Documentary match: Not corroborated (${vdrDocCount} chunk${vdrDocCount === 1 ? '' : 's'} retrieved)`
                : 'Documentary match: None';
        validationSignalLabel = 'Does not match documentary evidence';
    } else if (mildNegative && vdrDocCount < 2) {
        severity = 'weak';
        overridesSummary = true;
        documentaryMatchLabel = 'Documentary match: Weak';
        validationSignalLabel = 'Limited documentary support';
    } else if (posScore > 0 && vdrDocCount > 0) {
        severity = 'supported';
        documentaryMatchLabel = 'Documentary match: Present';
        validationSignalLabel = 'Partially corroborated';
    } else if (vdrDocCount > 0) {
        severity = 'partial';
        documentaryMatchLabel = 'Documentary match: Review citations';
        validationSignalLabel = 'Review AI narrative against documents';
    } else if (relatedIrlCount > 0 && vdrDocCount === 0) {
        severity = 'irl_only';
        overridesSummary = true;
        documentaryMatchLabel = 'Documentary match: None (IRL only)';
        validationSignalLabel = 'Related IRL context only';
    }

    return {
        severity,
        overridesSummary,
        documentaryMatchLabel,
        validationSignalLabel,
        reasons: [...new Set(reasons)].slice(0, 4),
        negativeScore: neg.score,
        positiveScore: posScore,
    };
}
